import { useState, useEffect } from "react";
import {
  browserSupportsWebAuthn,
  startRegistration,
} from "@simplewebauthn/browser";
import {
  getRegistrationOptions,
  verifyRegistration,
  getMyPasskeys,
  deletePasskey,
} from "../api/passkey.api";
import type { PasskeySummary } from "@abejon/shared";

export function PasskeyManager() {
  const [passkeys, setPasskeys] = useState<PasskeySummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState("");

  if (!browserSupportsWebAuthn()) return null;

  useEffect(() => {
    loadPasskeys();
  }, []);

  async function loadPasskeys() {
    try {
      const data = await getMyPasskeys();
      setPasskeys(data.passkeys);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }

  async function handleAdd() {
    setAdding(true);
    setError("");
    try {
      const { challengeId, options } = await getRegistrationOptions();
      const credential = await startRegistration({ optionsJSON: options });
      await verifyRegistration(challengeId, credential);
      await loadPasskeys();
    } catch (err) {
      if (err instanceof Error && err.name === "NotAllowedError") return;
      setError("Failed to register passkey");
    } finally {
      setAdding(false);
    }
  }

  async function handleDelete(id: string) {
    setDeletingId(id);
    try {
      await deletePasskey(id);
      setPasskeys((prev) => prev.filter((p) => p.id !== id));
    } catch {
      setError("Failed to delete passkey");
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <section className="mb-8">
      <h2 className="mb-3 text-lg font-semibold">Passkeys</h2>

      {loading ? (
        <div className="flex justify-center py-4">
          <div className="h-6 w-6 animate-spin rounded-full border-3 border-gray-200 border-t-gray-900" />
        </div>
      ) : passkeys.length > 0 ? (
        <div className="space-y-2">
          {passkeys.map((pk) => (
            <div
              key={pk.id}
              className="flex items-center justify-between rounded-lg border border-gray-200 px-4 py-3"
            >
              <div>
                <span className="text-sm font-medium font-mono">
                  {pk.credentialIdPreview}
                </span>
                <span className="ml-2 text-xs text-gray-500">
                  Added {new Date(pk.createdAt).toLocaleDateString()}
                </span>
              </div>
              <button
                onClick={() => handleDelete(pk.id)}
                disabled={deletingId === pk.id}
                aria-label={`Remove passkey ${pk.credentialIdPreview}`}
                className="text-sm text-red-600 hover:text-red-800 disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 rounded"
              >
                {deletingId === pk.id ? "Removing..." : "Remove"}
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-gray-500">No passkeys registered.</p>
      )}

      {error && (
        <p className="mt-2 text-sm text-red-600">{error}</p>
      )}

      <button
        onClick={handleAdd}
        disabled={adding}
        className="mt-3 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
      >
        {adding ? "Registering..." : "Add passkey"}
      </button>
    </section>
  );
}
